// lib/artifact-utils.ts
import { ArtifactItem, ArtifactType } from "@/types/Artifact";
import saveAs from "file-saver";
import * as htmlToImage from 'html-to-image';
import { toast } from "sonner";

export type ParsedArtifactData = {
  type: ArtifactType;
  language?: string;
  title: string;
  content: any;
};

export type ArtifactParseResult = {
  processedContent: string;
  artifacts: ParsedArtifactData[];
};

export type ArtifactStartInfo = {
  type: ArtifactType;
  title: string;
  language?: string;
};

/**
 * Detects the start of artifacts in streaming content
 */
export const detectArtifactStart = (content: string): ArtifactStartInfo[] => {
  const artifactStartRegex = /<SlatePanel\s+type="([^"]+)"(?:\s+language="([^"]+)")?(?:\s+title="([^"]+)")?/g;
  const starts: ArtifactStartInfo[] = [];
  let match;

  while ((match = artifactStartRegex.exec(content)) !== null) {
    const [, type, language, title] = match;
    
    starts.push({
      type: type as ArtifactType,
      language: language || undefined,
      title: title || generateDefaultTitle(type as ArtifactType),
    });
  }

  return starts;
};

/**
 * Parses content for artifact elements and extracts them
 */
export const parseArtifactElements = (content: string): ArtifactParseResult => {
  if (!content?.trim()) {
    return { processedContent: content, artifacts: [] };
  }

  const artifactRegex = /<SlatePanel\s+type="([^"]+)"(?:\s+language="([^"]+)")?(?:\s+title="([^"]+)")?>([^]*?)<\/SlatePanel>/g;
  
  let processedContent = content;
  const artifacts: ParsedArtifactData[] = [];
  let match;

  while ((match = artifactRegex.exec(content)) !== null) {
    const [fullMatch, type, language, title, rawContent] = match;
    
    try {
      const parsedContent = parseArtifactContent(type as ArtifactType, rawContent.trim());
      
      artifacts.push({
        type: type as ArtifactType,
        language: language || undefined,
        title: title || generateDefaultTitle(type as ArtifactType),
        content: parsedContent,
      });

      // Replace with placeholder for UI rendering
      const placeholder = `[[ARTIFACT_PLACEHOLDER_${artifacts.length - 1}]]`;
      processedContent = processedContent.replace(fullMatch, placeholder);
      
    } catch (error) {
      console.warn("Failed to parse artifact content:", error);
      // Keep original content if parsing fails
    }
  }

  return { processedContent, artifacts };
};

/**
 * Parses artifact content based on type
 */
const parseArtifactContent = (type: ArtifactType, rawContent: string): any => {
  switch (type) {
    case "code":
      return rawContent;
      
    case "sql":
      if (rawContent.includes("-- Result")) {
        const [query, resultPart] = rawContent.split("-- Result");
        try {
          const result = JSON.parse(resultPart.trim());
          return { query: query.trim(), result };
        } catch {
          return { query: query.trim(), result: resultPart.trim() };
        }
      }
      return rawContent;
      
    case "graph":
    case "table":
    case "map":
      try {
        return JSON.parse(rawContent);
      } catch {
        return rawContent;
      }
      
    default:
      return rawContent;
  }
};

/**
 * Generates default title for artifact type
 */
const generateDefaultTitle = (type: ArtifactType): string => {
  const titles: Record<ArtifactType, string> = {
    code: "Code Block",
    graph: "Chart",
    table: "Data Table",
    sql: "SQL Query",
    visualization: "Visualization",
    map: "Map",
    link: "Link"
  };
  
  return titles[type] || "Artifact";
};

/**
 * Replaces artifact placeholders with interactive buttons
 */
export const renderArtifactPlaceholders = (
  content: string, 
  artifacts: ParsedArtifactData[]
): string => {
  let result = content;
  
  artifacts.forEach((artifact, index) => {
    const placeholder = `[[ARTIFACT_PLACEHOLDER_${index}]]`;
    const button = `[[ARTIFACT_BTN_${Date.now()}_${index}|${artifact.type}|${artifact.title}]]`;
    result = result.replace(placeholder, button);
  });
  
  return result;
};

/**
 * Renders loading artifact placeholders
 */
export const renderLoadingArtifactPlaceholders = (
  content: string,
  loadingArtifacts: Array<{ type: string; title: string; index: number }>
): string => {
  let result = content;
  
  loadingArtifacts.forEach((artifact) => {
    const loadingButton = `[[LOADING_ARTIFACT_BTN_${artifact.index}|${artifact.type}|${artifact.title}]]`;
    // Insert at the end of content for now, or you could be smarter about placement
    result += `\n\n${loadingButton}`;
  });
  
  return result;
};

/**
 * Checks if content contains streaming artifact markers
 */
export const hasStreamingArtifacts = (content: string): boolean => {
  return /<SlatePanel\s+type="[^"]+"/g.test(content);
};

/**
 * Extracts streaming content for a specific artifact from the full content
 */
export const extractStreamingArtifactContent = (
  content: string, 
  artifactType: ArtifactType, 
  artifactTitle: string
): { content: string; language?: string } | null => {
  // Create a regex to match the specific artifact by type and title
  const escapedTitle = artifactTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const artifactRegex = new RegExp(
    `<SlatePanel\\s+type="${artifactType}"(?:\\s+language="([^"]+)")?(?:\\s+title="${escapedTitle}")?>([^]*?)(?:</SlatePanel>|$)`,
    'i'
  );
  
  const match = content.match(artifactRegex);
  if (!match) return null;
  
  const [, language, artifactContent] = match;
  
  return {
    content: artifactContent?.trim() || "",
    language: language || undefined
  };
};

/**
 * Extracts partial artifact from streaming content
 */
export const extractPartialArtifact = (content: string): ParsedArtifactData | null => {
  // Look for incomplete artifacts (no closing tag)
  const match = content.match(/<SlatePanel\s+type="([^"]+)"(?:\s+language="([^"]+)")?(?:\s+title="([^"]+)")?>([^]*?)$/);
  
  if (!match) return null;
  
  const [, type, language, title, partialContent] = match;
  
  return {
    type: type as ArtifactType,
    language: language || undefined,
    title: title || generateDefaultTitle(type as ArtifactType),
    content: partialContent.trim() || null,
  };
};

/**
 * Filters out artifacts that should stream to panel from message content
 */
export const filterStreamingArtifacts = (content: string): string => {
  // Remove non-graph artifacts from streaming content
  const nonGraphArtifactRegex = /<SlatePanel\s+type="(?!graph)([^"]+)"[^>]*>([^]*?)(?:<\/SlatePanel>|$)/g;
  return content.replace(nonGraphArtifactRegex, '');
};

// Export utilities
export const copyToClipboard = async (content: any): Promise<void> => {
  try {
    let textToCopy = "";
    
    if (typeof content === "string") {
      textToCopy = content;
    } else if (typeof content === "object") {
      textToCopy = JSON.stringify(content, null, 2);
    } else {
      textToCopy = String(content);
    }
    
    await navigator.clipboard.writeText(textToCopy);
    toast.success("Copied to clipboard");
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    toast.error("Failed to copy to clipboard");
  }
};

export const downloadArtifact = (artifact: ArtifactItem): void => {
  try {
    let content = "";
    let extension = ".txt";
    
    switch (artifact.type) {
      case "code":
        content = artifact.content;
        extension = artifact.language ? `.${artifact.language}` : ".txt";
        break;
        
      case "sql":
        if (typeof artifact.content === "object" && artifact.content.query) {
          content = artifact.content.query;
          extension = ".sql";
        } else {
          content = String(artifact.content);
          extension = ".sql";
        }
        break;
        
      default:
        content = typeof artifact.content === "string" 
          ? artifact.content 
          : JSON.stringify(artifact.content, null, 2);
        extension = ".json";
    }
    
    const filename = `${artifact.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}${extension}`;
    const blob = new Blob([content], { type: "text/plain" });
    saveAs(blob, filename);
    
  } catch (error) {
    console.error("Failed to download artifact:", error);
    toast.error("Failed to download artifact");
  }
};

export const downloadChart = async (containerId: string): Promise<void> => {
  try {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error('Chart container not found');
    }

    const dataUrl = await htmlToImage.toPng(container, { cacheBust: true });
    saveAs(dataUrl, `chart-${containerId}-${Date.now()}.png`);
    toast.success("Chart downloaded");
    
  } catch (error) {
    console.error('Failed to download chart:', error);
    toast.error("Failed to download chart");
  }
};

export const copyChartToClipboard = async (containerId: string): Promise<void> => {
  try {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error('Chart container not found');
    }

    const blob = await htmlToImage.toBlob(container, { cacheBust: true });
    if (!blob) throw new Error('Failed to generate image blob');

    const item = new ClipboardItem({ 'image/png': blob });
    await navigator.clipboard.write([item]);
    toast.success('Chart copied to clipboard');
    
  } catch (error) {
    console.error('Failed to copy chart:', error);
    toast.error("Failed to copy chart");
  }
};

/**
 * Converts table data to CSV format
 */
const tableToCSV = (headers: string[], rows: string[][]): string => {
  const escapeCSV = (value: any): string => {
    // Convert to string first
    const strValue = value === null || value === undefined ? '' : String(value);
    // Escape quotes and wrap in quotes if contains comma, quote, or newline
    if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
      return `"${strValue.replace(/"/g, '""')}"`;
    }
    return strValue;
  };

  const csvRows = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.map(escapeCSV).join(','))
  ];

  return csvRows.join('\n');
};

/**
 * Downloads table data as CSV file
 */
export const downloadTableAsCsv = (artifact: ArtifactItem): void => {
  try {
    if (artifact.type !== 'table') {
      throw new Error('Artifact is not a table');
    }

    const tableData = typeof artifact.content === 'string' 
      ? JSON.parse(artifact.content) 
      : artifact.content;

    const { headers = [], rows = [] } = tableData;

    if (!Array.isArray(headers) || !Array.isArray(rows)) {
      throw new Error('Invalid table structure');
    }

    const csvContent = tableToCSV(headers, rows);
    const filename = `${artifact.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.csv`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
    toast.success('Table downloaded as CSV');
    
  } catch (error) {
    console.error('Failed to download table:', error);
    toast.error('Failed to download table');
  }
};

/**
 * Copies table data to clipboard (CSV format)
 */
export const copyTableToClipboard = async (artifact: ArtifactItem): Promise<void> => {
  try {
    if (artifact.type !== 'table') {
      throw new Error('Artifact is not a table');
    }

    const tableData = typeof artifact.content === 'string' 
      ? JSON.parse(artifact.content) 
      : artifact.content;

    const { headers = [], rows = [] } = tableData;

    if (!Array.isArray(headers) || !Array.isArray(rows)) {
      throw new Error('Invalid table structure');
    }

    const csvContent = tableToCSV(headers, rows);
    await navigator.clipboard.writeText(csvContent);
    toast.success('Table copied to clipboard');
    
  } catch (error) {
    console.error('Failed to copy table:', error);
    toast.error('Failed to copy table');
  }
};